import { useMemo } from 'react';
import { DPA_DASHBOARD_POLLING_TIME_MS } from '../../settings';
import { useFetchOnPageWebsiteFailedChecksQuery } from '../service';
import useHydrateFailedChecks from './useHydrateFailedChecks';

type Params = {
    websiteGuid: string;
    clusterGuid: string;
    totalUrlsInCluster?: number;
    shouldLongPoll?: boolean;
    shouldRefetchOnMount?: boolean;
};

export default function useGetFailedChecksForCluster({ websiteGuid, clusterGuid, totalUrlsInCluster, shouldLongPoll, shouldRefetchOnMount }: Params) {
    const { data: failedChecks, isLoading: isLoadingFailedChecks } = useFetchOnPageWebsiteFailedChecksQuery(
        { w_guid: websiteGuid },
        {
            // The polling interval is in milliseconds!
            pollingInterval: shouldLongPoll ? DPA_DASHBOARD_POLLING_TIME_MS : 0,
            // refetchOnMount can take a number that will refetch if the cache value is older than the value
            // This is in seconds!
            refetchOnMountOrArgChange: shouldRefetchOnMount ? DPA_DASHBOARD_POLLING_TIME_MS / 1000 : false,
        }
    );

    const clusterFailedChecks = useMemo(
        () => (failedChecks ? failedChecks.find((failedCheck) => failedCheck.cluster_guid === clusterGuid) : undefined),
        [clusterGuid, failedChecks]
    );

    const partialFailedChecks = useMemo(
        () =>
            clusterFailedChecks
                ? clusterFailedChecks.failed_checks.map((check) => ({
                    testKey: check.test_key,
                    affectedUrls: check.affected_urls,
                    affectedClusters: 1,
                    lastFound: check.last_found,
                    sampleUrl: check.sample_url,
                }))
                : [],
        [clusterFailedChecks]
    );

    const { failedChecksWithDetails, isLoadingFailedChecksDetails } = useHydrateFailedChecks({
        partialFailedChecks,
        totalUrlsCount: totalUrlsInCluster,
    });

    const isLoading = isLoadingFailedChecks || isLoadingFailedChecksDetails;

    return {
        failedChecksWithDetails,
        isLoading,
    };
}
